import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import {
  useGetFactScoreSummary,
  useGetFactSources,
  useUpdateFact,
  useValidateFactScore,
} from '@/application/services/fact.service'
import { useGetEntityTranslationState } from '@/application/services/translation.service'
import { useAuthStore } from '@/application/stores/auth.store'
import { resolveLanguage } from '@/application/i18n/language'
import type { Fact, ScoreLetter, TranslationsWritePayload } from '@/domains/types'
import { toScoreLetter } from '@/domains/types'
import { NutriScore } from '@/ui/components/common/NutriScore'
import { ScoreGradeSelector } from '@/ui/components/common/ScoreGradeSelector'
import { Button } from '@/ui/components/common/Button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/ui/components/common/Dialog'
import { RichTextEditor } from '@/ui/components/common/RichTextEditor'
import { ImageField } from '@/ui/components/forms/ImageField'
import { Input } from '@/ui/components/forms/Input'
import { AuthorSelector, type AuthorState } from '@/ui/components/forms/AuthorSelector'
import { TranslationTabs } from '@/ui/components/translations/TranslationTabs'

const makeFactSchema = (translate: (key: string, opts?: Record<string, unknown>) => string) =>
  z.object({
    title: z
      .string()
      .min(1, translate('validation.required'))
      .max(200, translate('validation.max_chars', { count: 200 })),
    description: z
      .string()
      .min(1, translate('validation.description_required'))
      .max(20000, translate('validation.max_chars', { count: 20000 })),
    url: z.string().url(translate('validation.url_invalid')).or(z.literal('')).optional(),
  })

type FactFormData = z.infer<ReturnType<typeof makeFactSchema>>

interface EditFactDialogProps {
  fact: Fact | null
  leadId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Shared "edit fact" dialog, used by the management and public
 * `LeadFactsSection`s and by the live `LeadFacts` gallery — the three
 * surfaces that let someone edit a fact. Owns its own `useForm` + zod
 * schema, `useUpdateFact` wiring, score validation, and image/author
 * sub-fields, following the `EditLeadDialog` precedent.
 */
export function EditFactDialog({ fact, leadId, open, onOpenChange }: EditFactDialogProps) {
  const { t } = useTranslation()
  const factSchema = useMemo(() => makeFactSchema(t), [t])
  const { user } = useAuthStore()
  const canChangeAuthor = user?.role === 'streamer' || user?.role === 'admin'

  const { mutateAsync: updateFact } = useUpdateFact(leadId)
  const { mutateAsync: validateScore } = useValidateFactScore(leadId, fact?.id ?? '')
  const { data: factScoreSummary } = useGetFactScoreSummary(leadId, fact?.id ?? '')

  const [imageUrl, setImageUrl] = useState('')
  const [validatedScore, setValidatedScore] = useState<ScoreLetter | null>(null)
  const [author, setAuthor] = useState<AuthorState>({ id: null, name: '', username: null })
  const [translations, setTranslations] = useState<TranslationsWritePayload>({})
  const { data: entityState, refetch: refetchEntityState } = useGetEntityTranslationState(
    'fact',
    fact?.id ?? '',
  )
  // The `fact` prop can come from a locale-resolved list read (e.g.
  // `useGetAllFactsByLead`, which passes the admin's own UI locale as
  // `?lang=`) — its `title`/`description`/`url` may already be translated
  // text, not the true source. Editing must always start from the raw
  // source row, otherwise saving a translation would write the resolved
  // (translated) text back into the source columns. `useGetFactSources`
  // never sends `?lang=`, so it always returns the untouched source fields.
  const { data: sourceFacts, refetch: refetchSourceFact } = useGetFactSources(leadId)
  const sourceFact = useMemo(
    () => sourceFacts?.find((candidate) => candidate.id === fact?.id),
    [sourceFacts, fact?.id],
  )

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FactFormData>({ resolver: zodResolver(factSchema) })

  useEffect(() => {
    if (fact) {
      reset({
        title: sourceFact?.title ?? fact.title,
        description: sourceFact?.description ?? fact.description,
        url: sourceFact?.url ?? fact.url ?? '',
      })
      setImageUrl(fact.imageUrl ?? '')
      setValidatedScore(toScoreLetter(fact.validatedScore))
      setAuthor({
        id: fact.submittedByUserId ?? null,
        name: fact.submittedByUsername ?? fact.submittedByTwitchUsername ?? '',
        username: fact.submittedByUsername ?? null,
      })
      setTranslations({})
    }
  }, [fact, sourceFact, reset])

  const onSubmit = async (data: FactFormData) => {
    if (!fact) return
    try {
      const authorChanged = canChangeAuthor && !!author.id && author.id !== fact.submittedByUserId
      await updateFact({
        id: fact.id,
        ...data,
        imageUrl: imageUrl || null,
        translations,
        ...(authorChanged ? { submittedByUserId: author.id } : {}),
      })
      try {
        await validateScore(validatedScore)
      } catch {
        toast.error(t('lead_detail.fact_score_validate_error'))
      }
      // Refetch this dialog's OWN translation-state query before closing —
      // it may have been fetching in the background since the dialog
      // opened (before this edit), and TanStack Query doesn't otherwise
      // know to treat that in-flight request as outdated. `refetch()`
      // starts a new, superseding fetch — its result is what the query
      // ends up holding regardless of whether the earlier one resolves
      // first or last. Same fix as `EditLeadDialog` (lead.service.ts);
      // this dialog is likewise always mounted by its callers (only
      // Radix's internal presence toggles with `open`), so a stale
      // mount-time fetch can otherwise land after a save and revert the
      // translation-state badges shown on reopen.
      await Promise.all([refetchSourceFact(), refetchEntityState()])
      onOpenChange(false)
      setImageUrl('')
      setValidatedScore(null)
      toast.success(t('lead_detail.fact_updated'))
    } catch (error: unknown) {
      toast.error(
        t('lead_detail.fact_update_failed', {
          error: error instanceof Error ? error.message : String(error),
        }),
      )
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen)
        if (!nextOpen) reset()
      }}
    >
      <DialogContent size="wide" onPointerDownOutside={(event) => event.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-serif">{t('lead_detail.fact_edit_title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 pt-2">
          {fact && (
            <TranslationTabs
              sourceLocale={resolveLanguage(fact.sourceLocale ?? 'fr')}
              fields={[
                {
                  name: 'title',
                  labelKey: 'lead_detail.fact_title_label',
                  kind: 'input',
                  sourceValue: watch('title'),
                },
                {
                  name: 'description',
                  labelKey: 'lead_detail.fact_description_label',
                  kind: 'richtext',
                  sourceValue: watch('description'),
                  richTextEditorProps: { scrollable: true, compact: true },
                },
                {
                  name: 'url',
                  labelKey: 'lead_detail.fact_url_label',
                  kind: 'url',
                  sourceValue: watch('url') ?? '',
                },
              ]}
              entityState={entityState}
              onChange={setTranslations}
              disabled={isSubmitting}
              renderSourceFields={() => (
                <>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">
                      {t('lead_detail.fact_title_label')}{' '}
                      <span className="text-destructive">*</span>
                    </label>
                    <Input
                      {...register('title')}
                      placeholder={t('lead_detail.fact_title_placeholder')}
                    />
                    {errors.title && (
                      <p className="text-destructive text-xs">{errors.title.message}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">
                      {t('lead_detail.fact_description_label')}{' '}
                      <span className="text-destructive">*</span>
                    </label>
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <RichTextEditor
                          value={field.value}
                          onChange={field.onChange}
                          placeholder={t('lead_detail.fact_description_placeholder')}
                          compact
                          scrollable
                          onUploadError={(message) => toast.error(message)}
                        />
                      )}
                    />
                    {errors.description && (
                      <p className="text-destructive text-xs">{errors.description.message}</p>
                    )}
                  </div>
                </>
              )}
            />
          )}

          <div className="space-y-1.5">
            <label className="text-sm font-medium">{t('lead_detail.fact_url_label')}</label>
            <Input
              {...register('url')}
              type="url"
              placeholder={t('lead_detail.fact_url_placeholder')}
            />
            {errors.url && <p className="text-destructive text-xs">{errors.url.message}</p>}
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">{t('lead_detail.fact_image_label')}</label>
            <ImageField value={imageUrl} onChange={setImageUrl} />
          </div>
          {factScoreSummary?.averageScore && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">
                {t('lead_detail.fact_score_average_label')}
                {factScoreSummary.count > 0 && (
                  <span className="font-normal ml-1">
                    ({t('lead_detail.fact_score_voters', { count: factScoreSummary.count })})
                  </span>
                )}
              </label>
              <NutriScore grade={factScoreSummary.averageScore} size="sm" showLabel={false} />
            </div>
          )}
          {canChangeAuthor && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t('admin.form_author_label')}</label>
              <p className="text-sm text-muted-foreground">{t('admin.form_author_desc_fact')}</p>
              <AuthorSelector value={author} onChange={setAuthor} allowClear={false} />
            </div>
          )}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              {t('lead_detail.fact_score_validated_label')}
            </label>
            <ScoreGradeSelector
              value={validatedScore}
              onChange={setValidatedScore}
              disabled={isSubmitting}
            />
          </div>
          <DialogFooter className="pt-2">
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={isSubmitting}>
                {t('common.cancel')}
              </Button>
            </DialogClose>
            <Button type="submit" isLoading={isSubmitting}>
              {t('lead_detail.fact_edit_submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
